import { useEffect, useRef } from "preact/hooks";
import { bucketLabel, drill, exploreEras, useFacet, type Bucket } from "../../state/explore";
import { exploreScope, exploreWindow } from "../../state/store";

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOW_SHORT = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

/** The time axis of the slice: a bar strip by month (upcoming / short windows) or by
 *  year (past / all time; rolled up to decades once the span is long), with weekday and
 *  start-time rows for the live era. Tap a bar to narrow the window to it. */
export function TimePanel({ onDrill }: { onDrill?: () => void }) {
  const w = exploreWindow.value;
  const spanYears = spanOf(w.from, w.to);
  // Months for anything ≤ 3 years, years beyond that; the archive's 400-year span is
  // rolled up to decades client-side so the strip stays legible.
  const gran: "month" | "year" = exploreScope.value === "upcoming" || spanYears <= 3 ? "month" : "year";
  const series = useFacet(gran);
  const liveOnly = exploreEras.value.includes("live");
  const dow = useFacet("dow", { enabled: liveOnly });
  const hour = useFacet("hour", { enabled: liveOnly });

  const rollDecades = gran === "year" && series.buckets.length > 60;
  const bars = rollDecades ? toDecades(series.buckets) : fillGaps(series.buckets, gran);
  const maxN = bars.reduce((m, b) => Math.max(m, b.n), 1);
  const loadingFresh = series.loading && series.buckets.length === 0;
  // A four-century strip is wider than a phone; the recent end is where the data is, so
  // that is where it opens (the 1600s are a swipe away, not the first thing seen).
  const scrollRef = useRef<HTMLDivElement>(null);
  const firstKey = bars[0]?.key ?? "";
  const lastKey = bars[bars.length - 1]?.key ?? "";
  useEffect(() => {
    const el = scrollRef.current;
    if (el && gran === "year") el.scrollLeft = el.scrollWidth;
  }, [gran, firstKey, lastKey]);

  function tap(b: Bucket) {
    const dim = rollDecades ? "decade" : gran;
    if (b.n === 0) return;
    if (drill(dim, b)) onDrill?.();
  }

  return (
    <section class={`xp-panel xp-panel-time${series.loading ? " is-loading" : ""}`} aria-busy={series.loading}>
      <header class="xp-panel-head">
        <div class="xp-panel-titles">
          <h3 class="xp-panel-title">When</h3>
          <p class="xp-panel-hint">
            {rollDecades ? "By decade" : gran === "month" ? "By month" : "By year"} · tap a bar to narrow
          </p>
        </div>
      </header>
      {loadingFresh && <div class="xp-strip xp-skeleton" aria-hidden="true" />}
      {!loadingFresh && bars.length === 0 && <p class="xp-note">Nothing in this slice.</p>}
      {!loadingFresh && bars.length === 1 && (
        <p class="xp-note">
          All {bars[0].n.toLocaleString()} in {rollDecades ? `the ${bars[0].key}s` : bucketLabel(gran, bars[0])} — remove the
          time chip above to see the whole timeline.
        </p>
      )}
      {bars.length > 1 && (
        <div class="xp-strip-scroll" ref={scrollRef}>
          <div class="xp-strip" role="list">
            {bars.map((b, i) => {
              const label = rollDecades ? `${b.key}s` : bucketLabel(gran, b);
              const tick = tickFor(b.key, i, bars.length, gran, rollDecades);
              return (
                <button
                  type="button"
                  role="listitem"
                  key={b.key}
                  class={`xp-strip-bar${b.n === 0 ? " is-empty" : ""}`}
                  title={`${label}: ${b.n.toLocaleString()} concert${b.n === 1 ? "" : "s"}`}
                  aria-label={`${label}: ${b.n.toLocaleString()}`}
                  onClick={() => tap(b)}
                  disabled={b.n === 0}
                >
                  <span class="xp-strip-fill" style={{ height: `${Math.max(b.n > 0 ? 3 : 0, (b.n / maxN) * 100)}%` }} />
                  {tick && <span class="xp-strip-tick">{tick}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {liveOnly && (dow.buckets.length > 0 || hour.buckets.length > 0) && (
        <div class="xp-minis">
          {dow.buckets.length > 0 && (
            <MiniStrip
              title="Weekday"
              bars={Array.from({ length: 7 }, (_, d) => {
                const b = dow.buckets.find((x) => x.key === String(d));
                return { key: String(d), label: DOW_SHORT[d], n: b?.n ?? 0 };
              })}
            />
          )}
          {hour.buckets.length > 0 && (
            <MiniStrip
              title="Start time"
              bars={Array.from({ length: 24 }, (_, h) => {
                const b = hour.buckets.find((x) => x.key === String(h));
                return { key: String(h), label: h % 6 === 0 ? `${h}h` : "", n: b?.n ?? 0 };
              }).filter((_, h, arr) => h >= 8 || arr.slice(0, 8).some((x) => x.n > 0))}
              note={(() => {
                const u = hour.buckets.find((x) => x.key === "unknown");
                return u ? `${u.n.toLocaleString()} without a stated time` : undefined;
              })()}
            />
          )}
        </div>
      )}
    </section>
  );
}

function MiniStrip({ title, bars, note }: { title: string; bars: { key: string; label: string; n: number }[]; note?: string }) {
  const maxN = bars.reduce((m, b) => Math.max(m, b.n), 1);
  return (
    <div class="xp-mini">
      <div class="xp-mini-title">{title}</div>
      <div class="xp-mini-strip" role="img" aria-label={`${title}: ${bars.filter((b) => b.n > 0).map((b) => `${b.label || b.key} ${b.n}`).join(", ")}`}>
        {bars.map((b) => (
          <span key={b.key} class="xp-mini-bar" title={`${b.label || b.key}: ${b.n.toLocaleString()}`}>
            <span class="xp-mini-fill" style={{ height: `${(b.n / maxN) * 100}%` }} />
            <span class="xp-mini-tick">{b.label}</span>
          </span>
        ))}
      </div>
      {note && <div class="xp-mini-note">{note}</div>}
    </div>
  );
}

function spanOf(from: string | null, to: string | null): number {
  const f = from ? Number(from.slice(0, 4)) : 1600;
  const t = to ? Number(to.slice(0, 4)) : new Date().getFullYear() + 1;
  return Math.max(0, t - f);
}

/** Insert zero buckets for months/years with no concerts so the strip reads as a
 *  timeline, not a list of whatever happened to exist. Bounded to 400 cells. */
function fillGaps(buckets: Bucket[], gran: "month" | "year"): Bucket[] {
  if (buckets.length < 2) return buckets;
  const keys = buckets.map((b) => b.key).filter((k) => k !== "unknown").sort();
  if (!keys.length) return buckets;
  const byKey = new Map(buckets.map((b) => [b.key, b]));
  const out: Bucket[] = [];
  if (gran === "year") {
    const a = Number(keys[0]);
    const z = Number(keys[keys.length - 1]);
    for (let y = a; y <= z && out.length < 400; y++) {
      const k = String(y);
      out.push(byKey.get(k) ?? { key: k, k, n: 0, up: 0 });
    }
  } else {
    let [y, m] = keys[0].split("-").map(Number);
    const [zy, zm] = keys[keys.length - 1].split("-").map(Number);
    while ((y < zy || (y === zy && m <= zm)) && out.length < 400) {
      const k = `${y}-${String(m).padStart(2, "0")}`;
      out.push(byKey.get(k) ?? { key: k, k, n: 0, up: 0 });
      m++;
      if (m > 12) { m = 1; y++; }
    }
  }
  return out;
}

function toDecades(buckets: Bucket[]): Bucket[] {
  const by = new Map<string, Bucket>();
  for (const b of buckets) {
    if (b.key === "unknown") continue;
    const k = `${Math.floor(Number(b.key) / 10) * 10}`;
    const prev = by.get(k);
    if (prev) { prev.n += b.n; prev.up += b.up; }
    else by.set(k, { key: k, k, n: b.n, up: b.up });
  }
  const keys = [...by.keys()].map(Number).sort((a, b) => a - b);
  const out: Bucket[] = [];
  for (let d = keys[0]; d <= keys[keys.length - 1]; d += 10) {
    const k = String(d);
    out.push(by.get(k) ?? { key: k, k, n: 0, up: 0 });
  }
  return out;
}

/** Sparse axis labels: January + the year for months; every 5th/10th for years/decades. */
function tickFor(key: string, i: number, total: number, gran: "month" | "year", decades: boolean): string | null {
  if (gran === "month") {
    const m = Number(key.slice(5, 7));
    if (m === 1 || i === 0) return `${MONTHS_SHORT[m - 1]} ${key.slice(0, 4)}`;
    return total <= 14 ? MONTHS_SHORT[m - 1] : m === 7 ? MONTHS_SHORT[6] : null;
  }
  const y = Number(key);
  if (decades) return y % 50 === 0 ? String(y) : null;
  if (total <= 12) return String(y);
  return y % 5 === 0 ? String(y) : null;
}
